import type { Tier } from './contracts/primitives';

/** Feature keys (docs/07 §1). Code checks features, never plan names. */
export const FEATURES = {
  catalogStandard: 'catalog.standard',
  catalogRecent: 'catalog.recent',
  catalogVip: 'catalog.vip',
  toneChange: 'tone.change',
  queueUnlimited: 'queue.unlimited',
  sessionHost: 'session.host',
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

export const FEATURE_KEYS: readonly FeatureKey[] = Object.values(FEATURES);

export function isFeatureKey(value: string): value is FeatureKey {
  return (FEATURE_KEYS as readonly string[]).includes(value);
}

/**
 * How the feature set is enforced (docs/07 §1, decision 2026-09-08).
 *
 * - `access` — **phase 1**, the default: access is all-or-nothing. One seeded product
 *   (`plan_full`) carries every non-VIP feature, so the only question is whether the caller
 *   holds it. No song is locked (no `tier='vip'` song exists and `recent` is included), every
 *   tone chip is allowed and there is no queue cap.
 * - `matrix` — the full plan × tier × tone × cap matrix of §1, kept for phase 2. Turned on with
 *   `settings.entitlements.enforce = true`.
 */
export type EntitlementMode = 'access' | 'matrix';

/** `settings.entitlements.enforce` → mode. Off (the phase-1 default) means access-only. */
export function entitlementMode(enforce: boolean | null | undefined): EntitlementMode {
  return enforce === true ? 'matrix' : 'access';
}

/** The caller's features plus the mode they are enforced under, passed around as one value. */
export interface Entitlements {
  features: readonly string[];
  mode: EntitlementMode;
  /**
   * Set only for a session of the standalone web: today's free list (docs/13 §7.3). A stick never
   * has it, so a stick never plays anything on the strength of that list.
   */
  webFreeSongIds?: ReadonlySet<string>;
}

/** Access that does not come from the feature set: a song on the web's free list. */
export interface FreeAccess {
  webFree?: boolean;
}

/** Feature that unlocks songs of a given tier: `catalog.<tier>`. */
export function tierFeature(tier: Tier): FeatureKey {
  return `catalog.${tier}` as FeatureKey;
}

export function hasFeature(
  features: readonly string[] | null | undefined,
  key: FeatureKey,
): boolean {
  return !!features && features.includes(key);
}

/**
 * Phase-1 access check — the single question the product asks (docs/07 §1): does this caller
 * hold the one product (`plan_full`)? `session.host` is the feature every plan carries, so it
 * stands for "has access" in both modes. Used by `POST /tv/pair/approve`.
 */
export function hasAccess(features: readonly string[] | null | undefined): boolean {
  return hasFeature(features, FEATURES.sessionHost);
}

/**
 * `locked` as computed for SongDTOs. Phase 1 (`access`): nothing is ever locked — there are no
 * VIP songs and `recent` is included, so a lock badge could only ever be wrong.
 */
export function songLockedFor(
  features: readonly string[] | null | undefined,
  tier: Tier,
  mode: EntitlementMode,
): boolean {
  if (mode === 'access') return false;
  return !hasFeature(features, tierFeature(tier));
}

/**
 * May this caller play/enqueue a song of `tier`? A song on the web's free list always may; otherwise
 * phase 1 only asks "has access" (docs/13 §7.3).
 */
export function canPlayTier(
  features: readonly string[] | null | undefined,
  tier: Tier,
  mode: EntitlementMode,
  free: FreeAccess = {},
): boolean {
  if (free.webFree) return true;
  return mode === 'access' ? hasAccess(features) : hasFeature(features, tierFeature(tier));
}

/** The feature reported by `403 feature_required` when `canPlayTier` says no. */
export function requiredTierFeature(tier: Tier, mode: EntitlementMode): FeatureKey {
  return mode === 'access' ? FEATURES.sessionHost : tierFeature(tier);
}

/**
 * A tone other than the song's default requires `tone.change` in the full matrix; in phase 1
 * the −2..+2 chips are always allowed for a caller with access. A song on the web's free list may
 * change its tone with no plan at all (docs/13 §5.1).
 */
export function toneAllowedFor(
  features: readonly string[] | null | undefined,
  tone: string,
  defaultTone: string,
  mode: EntitlementMode,
  free: FreeAccess = {},
): boolean {
  if (tone === defaultTone || free.webFree) return true;
  return mode === 'access' ? hasAccess(features) : hasFeature(features, FEATURES.toneChange);
}

/** Per-user cap of queued items without `queue.unlimited` (docs/07 §1). */
export const DEFAULT_QUEUE_CAP = 5;

/** `null` = unlimited. Phase 1 has no cap at all. */
export function queueCapFor(
  features: readonly string[] | null | undefined,
  mode: EntitlementMode,
): number | null {
  if (mode === 'access') return null;
  return hasFeature(features, FEATURES.queueUnlimited) ? null : DEFAULT_QUEUE_CAP;
}

/**
 * Phase 1 (decision 2026-09-08, docs/07 §1) is all-or-nothing: one seeded product carrying every
 * non-VIP feature. Grants always point at it and the CMS never asks the operator to pick one.
 */
export const PHASE1_PRODUCT_ID = 'plan_full';

/** Features of `plan_full` — everything except `catalog.vip` (no VIP tier in phase 1). */
export const PHASE1_PRODUCT_FEATURES: readonly FeatureKey[] = [
  FEATURES.sessionHost,
  FEATURES.catalogStandard,
  FEATURES.catalogRecent,
  FEATURES.toneChange,
  FEATURES.queueUnlimited,
];
